import { z } from "zod";

// The version-stamped dataset format — the single extractor↔app coupling point (ADR 0001).
// The extractor validates on emit, a test in this package validates the committed artifact,
// and the web app parses it once at startup. Objects are strict: an unknown key in the dataset
// is a schema surprise to fail loudly on, not noise to tolerate.

/** An entity id as the game's own files spell one — lowercase, digits and underscores. Every
 * id in the dataset is a join key into another of its tables (a Research Subject, a Ship Item,
 * a unit, an ability), so the shape is declared once here rather than restated at each join. */
const EntityId = z.string().regex(/^[a-z0-9_]+$/);

/** A non-empty list of entity ids — the shape every prerequisite and requirement list takes.
 * Empty is never honest: a list the game states at all states at least one id, and a list it
 * does not state is absent (`.optional()` at the use site). */
const EntityIdList = z.array(EntityId).nonempty();

// Terminology is the game's own (verified in en.localized_text): TEC/Advent/Vasari/Eidolon
// are "races" (player_race_name.*), and the playable variants are "factions"
// (player_faction_name.* — TEC Enclave, Advent Wrath, …). loyalist/rebel are the data-file
// ids for the per-race factions; the UI shows the faction names, never these ids. The
// Eidolon (2.0.2.0, race id dlc3_herald) have ONE faction — the game still ids it
// dlc3_herald_loyalist, so `loyalist` is its data faction and no `rebel` twin exists.
export const RACES = ["tec", "advent", "vasari", "eidolon"] as const;
export const FACTIONS = ["loyalist", "rebel"] as const;

/** How many rungs a hull's experience Level ladder has — the game's own 1 to 10, declared
 * once (schema v36, Issue 49) so the schema, the extractor and the URL's bound agree. */
export const LEVEL_COUNT = 10;

/** How many Tiers each research Domain has — the game's own I to V, declared once so the
 * schema's bounds and the URL's tier-key grammar agree (the LEVEL_COUNT precedent). */
export const RESEARCH_TIER_COUNT = 5;

// The Class ladder (CONTEXT.md: Class — the game's own word, "frigate class vessels"), in
// display-sort order; the field is named `kind` only because `class` is a reserved word.
// Each value has a game display name in en.localized_text's unit_tag_name.* (Frigate,
// Cruiser, Capital Ship, Command Ship, …). Ship kinds come from `build.build_kind` (the
// game's own build-menu grouping — two in-game cruisers carry a `frigate` unit tag, so tags
// are NOT the ship ladder); strikecraft/structure/starbase come from the classification tag.
export const UNIT_KINDS = [
  "strikecraft",
  "corvette",
  "frigate",
  "cruiser",
  "capital_ship",
  "super_capital_ship",
  "titan",
  "structure",
  "starbase",
] as const;
// How a player comes to own the unit (CONTEXT.md: Obtained). Derived mechanically from
// which player-file source lists reference it (ADR 0004): build-menu lists → direct_build;
// buildable_strikecraft → hangar_built; ability_created_units only → ability_created;
// structure_builder.ship only → auto_built (the game spawns constructors to carry out
// structure orders); the explicit envoy exception → diplomacy.
export const OBTAINED_VALUES = [
  "direct_build",
  "hangar_built",
  "ability_created",
  "diplomacy",
  "auto_built",
] as const;
// DLCs the dataset's entities can require (amended ADR 0004). An id with an unmapped DLC
// prefix in a build list must fail extraction loudly, never silently join. `harbinger` is
// the DLC that licenses the Eidolon race — its in-files product id is "dlc_Herald"
// (uniforms/player.uniforms dlc_pickable_players), and unlike dlc.reinforcements.name the
// files ship no display name for it; "Harbinger" is the product's marketed name (Steam,
// 2026-09-03), the same naming source "reinforcements" uses. Since 2.0.2.0 the
// authoritative per-subject signal is the file-level `required_product` field — two
// un-prefixed Vasari cloak subjects are dlc-gated that way, so the id prefix alone no
// longer attributes DLC.
export const DLCS = ["reinforcements", "harbinger"] as const;
// The game's five exotic resource types (`.exotic` entities).
export const EXOTIC_TYPES = ["offense", "defense", "economic", "utility", "ultimate"] as const;
// The game's two research domains (every `.research_subject` declares one; the player files'
// research_domains carry exactly these two). The ids are race-neutral data keys — each race
// names its domains itself (TEC Military/Civilian, Advent Hostility/Harmony, Vasari
// Warfare/Empire, Eidolon Vanguard/Dominion, from <race>_research_domain_partial_name_* in
// en.localized_text).
export const RESEARCH_DOMAINS = ["military", "civilian"] as const;
// The game's two strikecraft kinds (strikecraft.uniforms) — every hangar can field both.
export const STRIKECRAFT_KINDS = ["interceptor", "bomber"] as const;

// The named gates that keep Formula Unverified honest (ADR 0003): each names the one in-game
// observation that resolves it (HANDOFF §3.7, Issue 11). A cell lists the flags it waits on;
// flipping a flag in the extraction verification state regenerates those cells as Computed
// with no UI change.
export const VERIFICATION_FLAG_IDS = [
  "burst_multiplicity",
  "torpedo_delivery",
  "research_stacking",
  "damage_formula",
  "corruption_buff_inheritance",
] as const;

// The three Corruption Tiers (CONTEXT.md: Corruption Tier — the game's own adjectives,
// Mild / Moderate / Extreme, in threshold order). "None" is the absence of a tier, a URL
// default, never a dataset value.
export const CORRUPTION_TIERS = ["mild", "moderate", "extreme"] as const;

// ADR 0003: every stat cell is one of four states making four different honesty claims.
// A bare/nullable number is not a legal stat — the schema is what stops the UI (or a future
// contributor) from improvising a 0 or a blank.
export const StatCellSchema = z.discriminatedUnion("state", [
  z
    .object({
      state: z.literal("computed"),
      value: z.number(),
      // Assumption Captions — the declared frame the value is true under (e.g. "excludes
      // item-gated mounts"). A visible caption, never a hidden default.
      captions: z.array(z.string().min(1)).nonempty().optional(),
    })
    .strict(),
  z
    .object({
      state: z.literal("not_applicable"),
      // A fact about the game ("structures have no speed"), not a data gap.
      reason: z.string().min(1),
    })
    .strict(),
  z
    .object({
      state: z.literal("formula_unverified"),
      flags: z.array(z.enum(VERIFICATION_FLAG_IDS)).nonempty(),
    })
    .strict(),
  z
    .object({
      state: z.literal("not_in_game_data"),
      // The value exists in the game but is not extractable from its data files.
      reason: z.string().min(1),
    })
    .strict(),
]);

/** The live resolution of each named verification gate (ADR 0003; HANDOFF §3.7, Issue 11) —
 * the ledger the extractor folds under, shipped in the dataset since schema v25 (ADR 0009) so
 * a page adding bonuses up itself renders Formula Unverified exactly as the extractor would.
 * Extraction owns the live values (its verification.ts); flipping one and re-extracting is
 * the whole release process for turning gated cells Computed. */
export const VerificationStateSchema = z
  .object({
    /** Whether a top-level burst weapon's `damage` is per shot (DPS = shots × damage /
     * cooldown) or per volley (DPS = damage / cooldown). Resolved per_volley by one in-game
     * tooltip reading. */
    burst_multiplicity: z.enum(["unverified", "per_shot", "per_volley"]),
    /** Whether the in-game tooltip confirms nominal torpedo DPS = damage / cooldown.
     * Torpedoes are interceptable child units, so even confirmed values are nominal. */
    torpedo_delivery: z.enum(["unverified", "nominal"]),
    /** How stat modifiers compose onto a base: (base + Σadditive) × (1 + Σscalar), observed
     * in game 2026-07-29 (the rule research, item and buff modifiers share). */
    research_stacking: z.enum(["unverified", "sum_then_scale"]),
    /** Whether damage mitigation follows the Art of War pipeline (Issue 09): effective
     * durability = max(Durability − Pierce, 0), scalar 1/(1 + 0.01·effective), pools deplete
     * shields → armor → hull, and the armor pool takes a second Pierce-immune scalar. */
    damage_formula: z.enum(["unverified", "art_of_war"]),
    /** Whether a unit that declares a Corruption pool but no tier-buff list of its own
     * receives its player file's list (ADR 0007). */
    corruption_buff_inheritance: z.enum(["unverified", "player_list_applies"]),
  })
  .strict();

/** Where a bonus comes from (schema v25, ADR 0009, CONTEXT.md Tech Set / Tier Bonus) — the
 * tag a page filters on when it adds a unit's stats up from the active sources. Attached
 * where the modifier is born, never reverse-engineered:
 * - `subject`: a Research Subject's own unit/weapon modifier block (by subject id);
 * - `tier`: a Domain Tier unlock's player bonus (the Tier Bonus), addressed as domain +
 *   0-based tier index — the same index `ResearchSubjectSchema.tier` and the
 *   `research.races[race][domain].tiers[]` array use (displayed I to V via `tierNames`);
 * - `unlock`: a unit- or weapon-local research-gated modifier, tagged with the ONE subject
 *   that gates it, so it joins that subject's closure (every shipped gate is a single subject;
 *   a compound gate fails extraction until decided);
 * - `corruption`: a Corruption Tier buff's modifier (ADR 0007), by tier;
 * - `item`: a Ship Item's own unit/weapon modifier (schema v34, ADR 0012, Issue 44), by item
 *   id AND the item level the value belongs to. An item's per-level `values` array publishes
 *   one entry per level, so the fold picks the level in force rather than storing a level
 *   axis of its own — the game levels items by research alone, so a Tech Set already decides
 *   how strong an equipped item is (ADR 0012: there is no fourth axis). */
const ModifierSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("subject"), id: EntityId }).strict(),
  z
    .object({
      kind: z.literal("tier"),
      domain: z.enum(RESEARCH_DOMAINS),
      tier: z
        .number()
        .int()
        .min(0)
        .max(RESEARCH_TIER_COUNT - 1),
    })
    .strict(),
  z.object({ kind: z.literal("unlock"), subjectId: EntityId }).strict(),
  z.object({ kind: z.literal("corruption"), tier: z.enum(CORRUPTION_TIERS) }).strict(),
  z
    .object({
      kind: z.literal("item"),
      id: EntityId,
      /** 1-based: the item level whose value this is (`values[level - 1]`). */
      level: z.number().int().positive(),
    })
    .strict(),
  /** A hull's own experience Level (schema v36, ADR 0012, Issue 49), by the level the entry
   * belongs to. The game's `levels.levels[]` states each level's figure as a TOTAL, not an
   * increment — level 10's weapon scalar is the +45% ADR 0012 records, not the 2.25 the ladder
   * sums to — so exactly ONE level's entries fold, the reached one's, and never the sum of the
   * levels below it. The stats a Level replaces outright (hull, armor, shields, Armor Strength,
   * antimatter, the hangar's capacity) are NOT here: a Level is the base for those, published
   * per level in `units[].levels` and picked before any modifier runs. */
  z
    .object({
      kind: z.literal("level"),
      /** 1-based, 1 to LEVEL_COUNT — the game's own ladder. */
      level: z.number().int().min(1).max(LEVEL_COUNT),
    })
    .strict(),
  /** One of the hull's own abilities (schema v37, ADR 0012, Issue 51), by the ABILITY level
   * the entry belongs to. A passive ability's standing buff modifies the ship that carries
   * it, and which of its levels is in force is decided by the hull's Level through
   * `abilities[].requiredUnitLevels` — or, on a research-levelled ability, by the Tech Set
   * through `abilities[].levelPrerequisites` (schema v38, Issue 54; no such ability carries
   * a modifier at 2.0.3.0, and a test fails if a patch adds one) — so exactly one level's
   * entries fold, and none at all while the ability is locked. Only a buff's UNCONDITIONAL values
   * are here: a value the game scales at runtime (per stack, per point of missing hull, per
   * point of damage dealt — its action value carries a `transform_type`) is a figure the
   * ability's own tooltip lines state, never a standing stat. */
  z
    .object({
      kind: z.literal("ability"),
      id: EntityId,
      /** 1-based: the ability level whose value this is (`values[level - 1]`). */
      level: z.number().int().positive(),
    })
    .strict(),
]);

/** One bonus that can touch a stat, in the game's own vocabulary (`value_behavior`:
 * additive | scalar) with its source tag. `caption` is the Assumption Caption the modifier
 * stamps on every cell it moves (a Corruption Tier's "Extreme Corruption"); `gate` is the
 * verification gate its APPLICABILITY waits on (the inherited tier-buff list). Applicability
 * (tags, weapon_type, race) is already resolved: a list is the unit's or weapon's own. The
 * fold is compute's foldStatCell / weapon folds: (base + Σadditive) × (1 + Σscalar). */
export const TaggedModifierSchema = z
  .object({
    source: ModifierSourceSchema,
    behavior: z.enum(["additive", "scalar"]),
    value: z.number(),
    caption: z.string().min(1).optional(),
    gate: z.enum(VERIFICATION_FLAG_IDS).optional(),
  })
  .strict();

/** The bonuses that can touch a unit's own folded stats (schema v25, Issue 22): one list per
 * stat the research fold can move, in fold order (the Race's subject and tier entries as the
 * player files list them, then the unit's own unlocks). Empty is honest: no source touches
 * it, or the unit lacks the stat (a structure has no Speed to research). Corruption Tiers
 * touch no unit stat at 2.0.3.0 (extraction refuses a tier unit modifier on a published
 * pool), so these lists carry no `corruption` source today. Applicability is resolved; the
 * page's fold over the active sources is compute's foldUnit. The fold under no source
 * reproduces the stored Base cells — checked at extraction on every unit and battery. */
const UnitModifiersSchema = z
  .object({
    hull: z.array(TaggedModifierSchema),
    armor: z.array(TaggedModifierSchema),
    shields: z.array(TaggedModifierSchema),
    speed: z.array(TaggedModifierSchema),
    armorStrength: z.array(TaggedModifierSchema),
    // The carrier's shared squadron capacity (schema v35, Issue 48): every bonus that can
    // grow it, which at 2.0.3.0 means the hangar Ship Items and one research-gated local
    // block (the Eidolon support platform). Empty on a hull nothing can grow, and on every
    // non-carrier — the list exists for all units so the fold has one shape to read.
    squadronCapacity: z.array(TaggedModifierSchema),
    // The antimatter pool (schema v36, Issue 49): ten Research Subjects across the four Races
    // grow it, all fleet-generic scalars. Empty on a unit with no antimatter block, whose cell
    // is Not Applicable in every frame. A Level does NOT appear here — it replaces the pool
    // outright, per level, in `units[].levels`.
    antimatter: z.array(TaggedModifierSchema),
    // The Cloak Detection row (schema v40, Issue 55, ADR 0013). Only a Ship Item moves it at
    // 2.0.3.0: the whole game declares `detection_quality` in exactly 16 files — the 14
    // innate unit blocks that ARE the Base cell, the Eidolon Cloak Detector Array (+5), and
    // the TEC Detection Droids, whose +4 is an activated buff and therefore never a list
    // entry. Empty on the 160 units nothing can move, and on the six that can it is one
    // entry — but the list exists for all units so the fold has one shape to read.
    cloakDetection: z.array(TaggedModifierSchema),
  })
  .strict();

/** The bonuses that can touch a weapon's folded quantities (schema v25, Issue 22): research
 * sources first, then the unit's Corruption Tier entries (mild, moderate, extreme). `damage`,
 * `cooldown` and `range` are always present; `bombingDamage` exactly on a planet-bombing
 * weapon (whose `damage` list is then empty — the game labels bombing output as its own
 * stat). A list a zero base cannot use (scalars alone on a 0-damage effect mount) is
 * already pruned, as the extractor's fold prunes it, so a page folds what it reads. The
 * page-side fold is compute's weaponDps / weaponNovaDamage / weaponRange over the active
 * sources; every stored Fully-Teched and tier cell equals that fold — checked at extraction
 * on every battery. */
const WeaponModifiersSchema = z
  .object({
    damage: z.array(TaggedModifierSchema),
    cooldown: z.array(TaggedModifierSchema),
    range: z.array(TaggedModifierSchema),
    bombingDamage: z.array(TaggedModifierSchema).optional(),
  })
  .strict();

/** What something costs in the game's three basic resources, all three always stated — the
 * files write every component, zeros included, so an absent one is a gap rather than a zero.
 * One shape for every price the dataset carries: a Research Subject's, a Ship Item's, and the
 * exotic factory's per-exotic fabrication recipe. */
export const ResourcePriceSchema = z
  .object({
    credits: z.number().nonnegative(),
    metal: z.number().nonnegative(),
    crystal: z.number().nonnegative(),
  })
  .strict();

const ExoticPriceSchema = z
  .object({
    exoticType: z.enum(EXOTIC_TYPES),
    count: z.number().int().positive(),
    // The exotic factory's price for fabricating ONE exotic of this type, per the unit's
    // race's recipe (the player files' buildable_exotics: races genuinely differ — Vasari
    // fabricate without credits). Multiply by count for the unit's total.
    fabrication: ResourcePriceSchema,
  })
  .strict();

/** One shipped art asset. `name` is the game's texture name — the file is `art/<name>.png`,
 * with `<name>150.png` / `<name>200.png` density variants where at150/at200 say the game
 * ships them. width/height are the base-scale pixel size, carried because assets are not
 * uniform (the envoy hud icon is 47×33 against the usual 85×40; main-view icons vary per
 * unit) and the UI needs real dimensions for aspect and layout stability. */
const ArtAssetSchema = z
  .object({
    name: EntityId,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    at150: z.boolean(),
    at200: z.boolean(),
  })
  .strict();

/** A unit's GUI art, resolved from the unit-skin gui block — never derived from the unit id
 * (25 roster references alias shared or renamed textures, e.g. every Reinforcements command
 * ship). tooltipPicture is absent when the game ships no tooltip-sized art for the unit:
 * honest absence, never a stand-in that isn't the art. */
const UnitArtSchema = z
  .object({
    hudIcon: ArtAssetSchema,
    tooltipPicture: ArtAssetSchema.optional(),
    mainViewIcon: ArtAssetSchema,
  })
  .strict();

/** One of the game's five exotic resources, by its own display name (exotic.<id>.name in
 * en.localized_text: Tauranite, Indurium, Andvar, Kalanide, Quarnium). The internal type
 * ids (offense/defense/…) are dataset keys only — the UI renders these names and icons. */
const ExoticInfoSchema = z
  .object({
    id: z.enum(EXOTIC_TYPES),
    name: z.string().min(1),
    description: z.string().min(1).optional(),
    smallIcon: ArtAssetSchema,
  })
  .strict();

/** One weapon battery on a unit: a distinct (weapon, gating) pairing with its mount
 * count and per-mount stat cells — the roster row's expandable "what actually shoots" layer. */
export const UnitWeaponSchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
    weaponType: z.enum(["normal", "planet_bombing"]),
    // The game's weapon tags (gauss/missile/beam/…), verbatim.
    tags: z.array(z.string().min(1)).nonempty(),
    firingType: z.enum(["projectile", "beam", "spawn_torpedo"]).optional(),
    // Flattened attack-target types this weapon can engage (from its target-type groups).
    // Empty for planet-bombing weapons, which cannot target ships.
    targets: z.array(z.string().min(1)),
    count: z.number().int().positive(),
    itemGated: z.boolean(),
    // The shop item that unlocks this battery (Issue 14): present exactly when itemGated —
    // the join key into the top-level items table, so the item listing and the weapon
    // table's "requires item" note corroborate each other.
    requiredItemId: EntityId.optional(),
    // The game's penetration stat (CONTEXT.md: Pierce — the player-facing term), on every
    // normal weapon (169/169 at 1.61.4.0); planet-bombing weapons cannot target ships and
    // carry none. Frame-invariant: no research modifies it (Issue 10).
    pierce: z.number().nonnegative().optional(),
    // True on a mount research adds (nine at 2.0.3.0: the Garda's light autocannon and
    // eight Eidolon batteries): Base stats exclude it, the Fully-Teched frame includes it.
    researchGated: z.boolean().optional(),
    // The one Research Subject that adds the mount (schema v28, Issue 23): present exactly
    // when researchGated — the join a Tech Set (Issue 24) decides the mount by, as an
    // `unlock` modifier rides its gating subject. A compound gate fails extraction.
    requiredSubjectId: EntityId.optional(),
    damage: z.number().nonnegative().optional(),
    cooldown: z.number().positive(),
    range: z.number().positive(),
    burstShots: z.number().int().positive().optional(),
    bombingDamage: z.number().nonnegative().optional(),
    populationDamage: z.number().nonnegative().optional(),
    // Per single mount (the unit columns carry the mount-summed values). Base only since
    // schema v29 (Issue 23): the Fully-Teched frame and the Corruption Tiers fold on the
    // page from `modifiers` over the mount's own quantities above.
    dps: StatCellSchema,
    novaDamage: StatCellSchema,
    // The tagged bonus lists the page folds (schema v25, ADR 0009): the battery's research
    // entries, then its unit's Corruption Tier entries per quantity.
    modifiers: WeaponModifiersSchema,
  })
  .strict();

/** One craft type a carrier's squadron slots can field. Squadron sizes are per race AND per
 * kind (TEC 6/4, Advent 8/6, Vasari 3/3, Eidolon 5/3); maxCraft = the squadrons this kind
 * can actually fill × squadronSize — the most of this craft the unit can field (slots are
 * shared, so the options are alternatives, not additive). Since 2.0.2.0 a carrier can cap
 * one kind below its shared capacity (carrier.strikecraft_kind_capacities — every Vasari
 * hangar fields at most ONE interceptor squadron); `maxSquadrons` is present exactly when
 * such a cap binds, and maxCraft already honours it.
 *
 * `maxCraft` and `maxSquadrons` are BASE-frame values since v35 (Issue 48): a hangar Ship
 * Item grows the shared capacity, so the page folds these the way it folds a stat cell, from
 * `kindCapacity` and the unit's `modifiers.squadronCapacity`. Folding under no source
 * reproduces the stored pair exactly, and extraction checks that on every carrier. */
const SquadronOptionSchema = z
  .object({
    kind: z.enum(STRIKECRAFT_KINDS),
    unitId: EntityId,
    name: z.string().min(1),
    squadronSize: z.number().int().positive(),
    maxCraft: z.number().int().nonnegative(),
    // The per-kind squadron cap, only when it is BELOW the carrier's shared capacity.
    maxSquadrons: z.number().int().nonnegative().optional(),
    // The RAW per-kind cap the unit file declares for this kind (schema v35, Issue 48),
    // present whenever it declares one — including the files' own "unlimited" 99. It has to
    // ship beside maxSquadrons because a Loadout can raise the shared capacity past a cap
    // that did not bind at Base, and the fold cannot re-decide from a figure that was
    // dropped for not binding.
    kindCapacity: z.number().int().nonnegative().optional(),
    // The craft itself is research-gated (2.0.2.0: the Eidolon bomber) — the hangar can
    // field it only after its unlock subject, so Base-frame counts carry this caveat.
    researchGated: z.literal(true).optional(),
  })
  .strict();

/** A carrier's hangar facts in the Base frame: capacity = the unit file's base value plus
 * the level-1 additive modifier. 0 is real and honest — TEC titans and the Argonev gain
 * squadrons only from items or levels, so at level 1 they field none. */
const UnitSquadronsSchema = z
  .object({
    capacity: z.number().int().nonnegative(),
    options: z.array(SquadronOptionSchema).nonempty(),
  })
  .strict();

/** Tooltip lines under the game's own heading for them (schema v16). A group without a
 * header is the ability's unscoped lines; a header ("On Beam Weapons:", "Bonus per Stack:")
 * scopes every line beneath it, so the two must never be flattened apart. */
const AbilityEffectGroupSchema = z
  .object({
    header: z.string().min(1).optional(),
    lines: z.array(z.string().min(1)).nonempty(),
  })
  .strict();

/** An ability's numeric half (Issue 15), shared by unit abilities and item-granted ones so
 * the two can never drift apart. */
const abilityNumbers = {
  // How many levels the ability has (1, 2, 3 or 5) — from the action data source's own
  // level_count, never assumed. Values below carry one figure per level.
  levelCount: z.number().int().positive(),
  // The game's ability-header facts, game-labelled and level-aware: "Cooldown: 90 seconds",
  // "Antimatter Cost: 75", "Requires Level: 0 / 2 / 4". Measured: none of these is ever
  // also a tooltip line, so they add facts rather than repeating them. Empty when the
  // ability is passive and ungated.
  facts: z.array(z.string().min(1)),
  // The composed effect numbers (Issue 15): the game's own words and figures, levels
  // joined " / " with unchanging values collapsed to one figure. Empty is honest absence —
  // 32 abilities ship no tooltip lines at all, and one unlabeled modifier blanks the whole
  // ability rather than misstating it (the research and item rule).
  effects: z.array(AbilityEffectGroupSchema),
} as const;

const UnitAbilitySchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
    // The game's own ability description, localized through the ability gui block. Absent
    // when the game ships none (2.0.2.0: the four cloak abilities carry name + icon only)
    // — honest absence, never an empty string, mirroring unit descriptions.
    description: z.string().min(1).optional(),
    icon: ArtAssetSchema,
    /** The hull level each ability level needs (schema v37, Issue 51) — the game's own
     * `min_required_unit_levels`, one entry per ability level, non-decreasing. Present on
     * the 132 abilities whose level the hull's Level decides (`level_source:
     * internal_level`) and absent on the one that takes its level from research (the
     * Eidolon support capital's cloak), which is therefore never level-locked.
     *
     * Entry i is the hull level ability level i+1 needs, so the level in force is the LAST
     * entry the hull has reached and the ability is LOCKED while even the first is out of
     * reach — 33 of the 133 abilities on leveled hulls are, at level 1. A 0 means no level
     * is required. The composed "Requires Level" fact states the same array in the game's
     * own words; this is its structured twin, because deciding what is in force by parsing
     * our own display string is how two surfaces start to disagree. */
    requiredUnitLevels: z.array(z.number().int().min(0).max(LEVEL_COUNT)).nonempty().optional(),
    /** The Research Subjects each ability level needs (schema v38, Issue 54) — the game's
     * own `level_prerequisites`, one entry per ability level, each the flat AND of subject
     * ids that level is gated on and `[]` where the level is ungated.
     *
     * Present on the 47 published abilities whose level RESEARCH decides (`level_source:
     * research_prerequisites_per_level`) and absent on every other, so the two level fields
     * are mutually exclusive by construction — extraction fails if an ability's declared
     * source and its fields disagree. The level in force is the last entry whose gate the
     * reader's Tech Set holds, and an ability whose FIRST entry is gated (43 of the game's
     * 51) cannot be used at all until that subject is ticked. Structured rather than parsed
     * back out of the composed Unlocks line, for the reason requiredUnitLevels is: deciding
     * what is in force by reading our own display string is how two surfaces start to
     * disagree. */
    levelPrerequisites: z.array(z.array(EntityId)).nonempty().optional(),
    ...abilityNumbers,
  })
  .strict();

/** The ability a ship item grants (schema v17). Unlike a unit ability, its identity fields
 * are OPTIONAL: most item-granted abilities ship a deliberately sparse gui because the
 * ITEM's own name, description and icon are their display surface. Issue 14 read that as
 * "no block to publish"; that was right while abilities were text-only and became wrong the
 * moment they carried numbers, because the numbers have nowhere else to live. 45 of the
 * shipped items are in this shape, including every consumable. */
const GrantedAbilitySchema = z
  .object({
    id: EntityId,
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    icon: ArtAssetSchema.optional(),
    ...abilityNumbers,
  })
  .strict();

// --- Unit items (Issue 14): the per-unit shop listing — a shared table + per-unit refs ---

/** One item level's composed facts. Effects follow the research Effect Line rules
 * (game-worded labels and numbers; the line ordering and glue are ours). Empty is honest
 * absence: ability-carrying, engine-flag, and description-only items compose no stat
 * lines, and one unlabeled modifier blanks the whole item rather than misstating it. */
const UnitItemLevelSchema = z
  .object({
    effects: z.array(z.string().min(1)),
    // Research subjects unlocking this level (levels past the first; flat AND, mirroring
    // research prerequisites — shipped data never uses alternative sets).
    prerequisites: EntityIdList.optional(),
    // Consumables: uses one purchase holds at this level (the game's "Uses:" — per level,
    // TEC consumables gain uses as their research levels unlock).
    consumableStackCount: z.number().int().positive().optional(),
  })
  .strict();

/** A buyable ship item (the game's ship_component), shared across the units that can buy
 * it. Race-scoped by the faction shop rosters; only items at least one roster unit can buy
 * are emitted (reachability rule — files on disk are not content). */
const UnitItemSchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
    // Absent for items whose loc description value is empty (the retrofit-bay precedent).
    description: z.string().min(1).optional(),
    race: z.enum(RACES),
    // Which of the race's faction shop rosters list it (usually both).
    factions: z.array(z.enum(FACTIONS)).nonempty(),
    // The shop tab the game files assign, with the game's own localized name.
    buildGroup: z.object({ id: z.string().min(1), name: z.string().min(1) }).strict(),
    // The hud icon only: item tooltip PICTURES exist in the game files (326 items) but
    // are large splash art the listing never renders — shipping them tripled the art
    // payload (116 → 302 MB measured), so they stay out until a UI needs them.
    hudIcon: ArtAssetSchema,
    // Absent price components are 0 in the files' own read (Vasari items omit credits).
    price: ResourcePriceSchema,
    exoticPrice: z.array(ExoticPriceSchema),
    buildTime: z.number().nonnegative(),
    maxCountOnUnit: z.number().int().positive().optional(),
    // The game's has_weapon owner constraint, with the tag's display name: the listing
    // includes such an item whenever the hull can FIT the weapon, so the note matters
    // exactly where the qualifying battery is itself item-gated (the Khevarkov's gauss).
    requiresWeapon: z
      .object({ tag: z.string().min(1), name: z.string().min(1) })
      .strict()
      .optional(),
    // Research subjects gating the purchase (flat AND, like research prerequisites).
    buildPrerequisites: EntityIdList.optional(),
    neededItems: EntityIdList.optional(),
    mutuallyExclusiveItems: EntityIdList.optional(),
    // The ability the item grants, displayed like unit abilities. Present whenever the game
    // gives it anything to say — its own display surface, its numbers, or both; absent only
    // when the item grants no ability or the ability states nothing (3 items).
    ability: GrantedAbilitySchema.optional(),
    // levels.length is the game's item_level_count (1 for non-leveled items); higher
    // levels arrive free via research, never re-bought.
    levels: z.array(UnitItemLevelSchema).nonempty(),
  })
  .strict();

/** One tier buff as the card states it (schema v24, Issue 20): the game's name and
 * description, whether the game files it as a benefit or a penalty, its tooltip lines in
 * the game's order (label + rendered value), and the mutation labels it sets ("Hull Regen
 * Disabled"). A buff with no lines tells its story through its description. */
const CorruptionTierBuffSchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
    scope: z.enum(["positive", "negative"]),
    description: z.string().min(1),
    lines: z.array(z.object({ label: z.string().min(1), value: z.string().min(1) }).strict()),
    mutations: z.array(z.string().min(1)),
  })
  .strict();

/** One tier of a unit's Corruption block: the tier's resolved buffs for the card (v24),
 * positive list first. The cells a tier moves fold on the page (v29, Issue 23) from the
 * Corruption entries on each battery's lists, so the block stores none. */
const CorruptionTierSchema = z
  .object({
    buffs: z.array(CorruptionTierBuffSchema).nonempty(),
  })
  .strict();

/** The Orbit Slot a Structure consumes (CONTEXT.md: Orbit Slot), from the unit file's
 * `structure` block: the game's two kinds, or the starbases' `virtual_slot_type` — a
 * military slot the planet does not count. */
export const SLOT_TYPES = ["civilian", "military", "virtual"] as const;

/** One Provides line (schema v30, Issue 30; CONTEXT.md: Provides): what a Structure does
 * for its owner, in the game's own tooltip header (`label`, a loc string with its trailing
 * colon trimmed like every other composed label) and the game's own names for what it
 * states (`value`). `caption` is an Assumption Caption where the file gates the line — a
 * factory that exists only once a named ship item is installed. A list of facts, not a
 * stat: a unit without a line has no cell at all, never a Not Applicable one.
 *
 * `value` is OPTIONAL since v31 (Issue 31): a flag structure's line is a whole statement
 * the game already words ("Phase Gate in Gravity Well") over a file that carries no figure,
 * so the line is its label alone. An empty string is not the same thing and stays refused;
 * a value-less line renders as an affirmation of its own label, never as a blank.
 *
 * `requiredItems` is the Loadout gate (schema v35, Issue 48, ADR 0012): the Ship Items whose
 * `unit_mutations` open this line's job block, any ONE of which suffices. A line carrying it
 * is stated exactly when the Loadout holds one of them and is absent otherwise — the gate
 * moved from extraction time to fold time, so the dataset says WHICH item opens the line
 * instead of deciding once for a reader who has equipped nothing. Absent on an unconditional
 * line, which is most of them, and on the one ability-gated line (the Sova Carrier's factory:
 * the hull always has that ability, so the capability is unconditionally true and the line
 * states it with an Assumption Caption instead). */
const ProvidesLineSchema = z
  .object({
    label: z.string().min(1),
    value: z.string().min(1).optional(),
    caption: z.string().min(1).optional(),
    requiredItems: z.array(z.string().min(1)).nonempty().optional(),
  })
  .strict();

/** A Structure's orbit facts (schema v28, Issue 29): present exactly when the unit's Class is
 * Structure or Starbase — the fact is per kind, and UnitSchema refuses the block elsewhere.
 * `slotCost` is the "Orbit Slot Cost" the game's build tooltip states (1 to 15), Computed for
 * a slotted structure and Not Applicable for a starbase (a virtual slot has no cost — never
 * zero, ADR 0003). */
const UnitStructureSchema = z
  .object({
    slotType: z.enum(SLOT_TYPES),
    slotCost: StatCellSchema,
  })
  .strict();

/** A hull's Cloaking (schema v41, Issue 56, ADR 0013): the ability the unit file's own
 * `cloak_ability` names, and the cloak quality each of that ability's levels reaches.
 *
 * The quality is NOT a `unit_modifiers` entry on the unit, so it joins none of ADR 0009's
 * tagged lists — it is an ability action value per level, and it resolves the way a Ship
 * Item's per-level values do: the selection decides which level is in force, and that
 * level's entry is the whole answer (never a sum of the rungs below it).
 *
 * A quality is a raw number, not a rung: the rung is what is left after an enemy's Cloak
 * Detection is subtracted, which is why the Anathema's crest publishes 5 against a ladder
 * the game names 0 to 4. Naming a rung is therefore an assumption, and the cell carries it
 * as an Assumption Caption. */
const UnitCloakSchema = z
  .object({
    /** The ability whose level decides the quality. Present in `abilities` where the hull
     * carries it itself (the four research-gated cloaks) and absent where a Ship Item
     * grants it (the Anathema's cloaking crest) — a hull whose cloak ability it does not
     * carry cannot cloak until that item is fitted. */
    abilityId: EntityId,
    /** One entry per ability level, in level order: `cloak_quality_value` from the
     * ability's action data source. [2] on the two Vasari corvettes, [3, 4] on the Kaziel
     * Shade, [2, 3] on the Kalizaeth Profaner, [5] on the Anathema's crest. */
    quality: z.array(z.number()).nonempty(),
  })
  .strict();

/** A unit's Corruption block (schema v21, ADR 0007): present exactly when the unit file
 * declares a Corruption pool — 156 roster units; the eight strikecraft and two Envoys
 * carry none and publish no block. */
export const UnitCorruptionSchema = z
  .object({
    // The pool (Issue 20): its size (the Corruption row's own figure), how fast it drains
    // per second, and how long after the last gain it waits before draining.
    maxCorruption: z.number().positive(),
    decayRate: z.number().nonnegative(),
    decayDelay: z.number().nonnegative(),
    // The pool fractions each tier starts at — the unit's own where declared, else its
    // player file's.
    thresholds: z
      .object({
        mild: z.number().min(0).max(1),
        moderate: z.number().min(0).max(1),
        extreme: z.number().min(0).max(1),
      })
      .strict(),
    // The game's per-race sentence above the pool (Eidolon vs everyone else).
    blurb: z.string().min(1),
    tiers: z
      .object({
        mild: CorruptionTierSchema,
        moderate: CorruptionTierSchema,
        extreme: CorruptionTierSchema,
      })
      .strict(),
  })
  .strict();

/** One rung of a hull's Item Slot capacity (schema v34, ADR 0012, Issue 44), from the unit
 * file's `items.levels[]`: how many Ship Items the hull can carry, and the research that
 * raises it. The FIRST entry is the hull's own capacity (never gated); a later entry states a
 * higher count its `prerequisites` unlock. It is a RESEARCH fact, not a level one — one hull
 * of 39 (the Anarkrul Wanderer, 6 → 8) moves at all, and it moves on a research subject. */
const ItemSlotsSchema = z
  .object({
    count: z.number().int().nonnegative(),
    // Research subjects raising the count (flat AND, like every other prerequisite list).
    // Absent on the first entry, which is what the hull carries with no research at all.
    prerequisites: EntityIdList.optional(),
  })
  .strict();

/** One rung of a hull's experience Level ladder (schema v36, ADR 0012, Issue 49) — the stats a
 * Level does not modify but REPLACES. `units[].levels` carries all ten in order, index 0 being
 * level 1, and level 1's row is exactly the unit's own Base cells (checked at extraction on
 * every leveled hull, and again by the whole-roster Base-fold invariant).
 *
 * hull / armor / shields / armorStrength come from the unit file's `health.levels[]`, which is
 * a per-level table of totals. antimatter and squadronCapacity come from `levels.levels[]`,
 * whose per-level `max_antimatter` and `max_squadron_capacity` additives are ALSO totals — 245
 * at level 2 is the whole pool, not 225 + 245 — so they are a base per level, not a ladder to
 * sum. What that block states as a genuine modifier (the weapon damage and cooldown scalars)
 * ships on the batteries' own lists tagged `level`, and folds the ordinary way. */
const UnitLevelSchema = z
  .object({
    hull: z.number().positive(),
    armor: z.number().nonnegative(),
    shields: z.number().nonnegative(),
    /** Absent exactly where the unit's own `armorStrength` cell is Not Applicable (no armor
     * pool) — the game's per-level table omits the field on the same hulls level 1 does. */
    armorStrength: z.number().nonnegative().optional(),
    antimatter: z.number().nonnegative(),
    /** Present exactly on a hull with a carrier block: the shared squadron capacity this level
     * gives it before any hangar Ship Item grows it (Issue 48's fold reads it as its base). */
    squadronCapacity: z.number().int().nonnegative().optional(),
  })
  .strict();

export const UnitSchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
    // Localized through the same skin-gui join as the name. Absent when the game's own
    // description is empty (all strikecraft and starbases) — never an empty string.
    description: z.string().min(1).optional(),
    race: z.enum(RACES),
    kind: z.enum(UNIT_KINDS),
    obtained: z.enum(OBTAINED_VALUES),
    // Which of the race's Factions can obtain the unit (rosters differ by Faction —
    // titans, titan foundries, command ships; the Eidolon have exactly one Faction).
    factions: z.array(z.enum(FACTIONS)).nonempty(),
    // True for units with XP levels (capitals/titans/command ships). The Base frame reads
    // level 1; the table header declares the frame (no per-row marker, user decision).
    leveled: z.boolean(),
    // Present only on units whose entities the base game does not license (amended ADR 0004).
    dlc: z.enum(DLCS).optional(),
    art: UnitArtSchema,
    // Cost facts as cells: hangar-built strikecraft have no build block at all — their cost
    // cells are Not Applicable, never zero (ADR 0003).
    credits: StatCellSchema,
    metal: StatCellSchema,
    crystal: StatCellSchema,
    supplyCost: StatCellSchema,
    buildTime: StatCellSchema,
    // Exotic components (capitals/titans/command ships/starbases). Cost-derived cells caption
    // their exclusion — exotics have no credit price to sum honestly.
    exoticPrice: z.array(ExoticPriceSchema),
    // The game's threat-accounting weight on units that are not fleet-built (structures,
    // strikecraft, deployables) — distinct from fleet supply and shown as its own fact.
    virtualSupplyCost: z.number().positive().optional(),
    weapons: z.array(UnitWeaponSchema),
    // Present exactly when the unit file carries a carrier block (33 units).
    squadrons: UnitSquadronsSchema.optional(),
    // By name only in v1 (locked §2 default; ability detail pages are post-MVP).
    abilities: z.array(UnitAbilitySchema),
    // Ids into the top-level items table (Issue 14): every shop item this unit can buy —
    // faction roster ∩ required_unit_tags ∩ owner constraint — in shop-tab order then
    // game-name order. Empty for the roster's 100 shopless units (no items block; the
    // install's other 3 block-carrying units are non-roster).
    items: z.array(EntityId),
    // How many of those items the hull can carry at once (schema v34, ADR 0012): the game's
    // `max_ship_component_count`, one entry per capacity rung. Empty exactly where `items`
    // is — a hull with no items block has no shop and no slots.
    itemSlots: z.array(ItemSlotsSchema),
    // The unit's own attack-target type (Issue 10): what weapons' target lists match
    // against. Three roster units declare none (both envoys, the TEC titan factory) —
    // their inbound counter direction is an honest data gap.
    targetType: EntityId.optional(),
    // The Can-Hit graph's outbound edges (Issue 10): roster units at least one Base-frame
    // ship-targeting battery can engage (same inclusion rule as the DPS aggregates), in
    // roster order. The inbound direction is this list's client-side inverse.
    canHit: z.array(EntityId),
    // Base frame (CONTEXT.md): zero research, level 1 — health.levels[0] in the game files.
    hull: StatCellSchema,
    armor: StatCellSchema,
    shields: StatCellSchema,
    // Corvettes lack the field and the game declares no default — Not In Game Data, never 0.
    durability: StatCellSchema,
    // Declared armor strength, or the game fact that the unit spawns without an armor pool
    // (Issue 09) — ten Advent hulls (eight ships and both strikecraft) omit the field,
    // always alongside a zero pool; eight Advent structures declare it WITH a zero pool,
    // so declared values always publish.
    armorStrength: StatCellSchema,
    // The Corruption pool's size (Issue 17, CONTEXT.md Corruption): `max_corruption` in the
    // unit file, or the game fact that the unit carries no pool — the eight strikecraft and
    // the two Envoys. Frame-invariant: no research modifies it, so no folded twin.
    corruptionPool: StatCellSchema,
    // The unit's Cloak Detection (schema v39, Issue 53, ADR 0013): its own innate
    // `detection_quality`, the one detection sibling the game gives a `tooltip.unit.*` label.
    // Computed 3 on the 14 carriers (all four Races' two research labs, plus six ships) and
    // Not Applicable "has no cloak detection" on the other 152 — never a 0, which would
    // claim a unit sees through cloaks it cannot touch. Frame-invariant here: the innate
    // entries are all ungated, so there is no folded twin.
    cloakDetection: StatCellSchema,
    // The antimatter pool (schema v36, Issue 49): what the ship spends on abilities. Base
    // frame, so level 1 on a leveled hull — the 33 leveled hulls declare 0 in their own
    // antimatter block and take the whole pool from their level table. Not Applicable on the
    // 109 roster units with no antimatter block at all.
    antimatter: StatCellSchema,
    speed: StatCellSchema,
    // Derived columns (Issue 03, the Insight Layer; ehp joined in Issue 09 — "EHP vs
    // 0 Pierce", gated on damage_formula until the in-game damage reading lands).
    range: StatCellSchema,
    dps: StatCellSchema,
    novaDamage: StatCellSchema,
    dpsPerSupply: StatCellSchema,
    dpsPerCost: StatCellSchema,
    costPerHp: StatCellSchema,
    ehp: StatCellSchema,
    bombingRate: StatCellSchema,
    populationRate: StatCellSchema,
    // The Corruption card's block (schema v21 / v24, ADR 0007): the pool, thresholds,
    // blurb and each tier's buffs. Absent exactly when the unit has no pool. What a tier
    // moves — Eidolon weapon damage into DPS / Nova Damage / the ratios, TEC weapon range into
    // Range; Advent and Vasari tiers touch none — folds on the page from the batteries'
    // lists (v29, Issue 23) by the same sum-then-scale rule.
    corruption: UnitCorruptionSchema.optional(),
    // The hull's own Cloaking (schema v41, Issue 56, ADR 0013), from the unit file's own
    // `cloak_ability` — the game's declaration of which six ships can hide. Absent on the
    // 160 that cannot, and on the Thalkerin Revenant, whose Cloak Generator ship item is in
    // no faction's shop at 2.0.3.0 (declared dead in extraction, with a loud gate if a patch
    // stocks it). Present is NOT "can cloak now": the page asks which level of `abilityId`
    // the reader's selection puts in force and reads that entry of `quality`, and four of
    // the six cannot cloak until a Research Subject is ticked or the granting item is fitted.
    cloak: UnitCloakSchema.optional(),
    // The ten rungs of the hull's experience Level (schema v36, ADR 0012, Issue 49), in order,
    // index 0 being level 1. Present exactly on the 33 hulls `leveled` is true of — every
    // capital ship, Command Ship and titan, and nobody else (refined below, so a leveled hull
    // without its table, or a table on a hull with no levels, fails the parse).
    levels: z.array(UnitLevelSchema).length(LEVEL_COUNT).optional(),
    // The tagged bonus lists behind the unit's own folded cells (schema v25, ADR 0009): the
    // Tech Set's data half. The page folds them over the Base cells above (v29, Issue 23);
    // no Fully-Teched twin is stored. The weapon-fed cells (DPS, Nova Damage, Range, the ratios,
    // Bombing) fold from each battery's own `weapons[].modifiers`.
    modifiers: UnitModifiersSchema,
    // What this unit does for its owner (CONTEXT.md: Provides; ADR 0008 as amended v33), in
    // the game's order — the unit file's own key order. Empty where the file states nothing
    // the vocabulary composes, which is most of the roster. It sits on the UNIT rather than
    // inside the structure block because the game prints these lines for ships too: its own
    // build preview lists what a Jusotra Fabricator Cruiser can build.
    provides: z.array(ProvidesLineSchema),
    // The Structures sheet's own facts (schema v28, Issue 29, ADR 0008): the Orbit Slot the
    // structure consumes. Present on every Structure and Starbase, on no ship — refined
    // below, so a structure without its block or a ship carrying one fails the parse.
    structure: UnitStructureSchema.optional(),
  })
  .strict()
  .superRefine((unit, ctx) => {
    const isStructure = unit.kind === "structure" || unit.kind === "starbase";
    if (isStructure && unit.structure === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["structure"],
        message: `${unit.id} is a ${unit.kind} without its structure block`,
      });
    }
    if (!isStructure && unit.structure !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["structure"],
        message: `${unit.id} is a ${unit.kind} and cannot carry a structure block`,
      });
    }
    // A Level's table and the `leveled` flag are one fact stated twice (Issue 49), so they
    // cannot drift: the 33 hulls with XP levels carry ten rungs, and nobody else carries any.
    if (unit.leveled !== (unit.levels !== undefined)) {
      ctx.addIssue({
        code: "custom",
        path: ["levels"],
        message: unit.leveled
          ? `${unit.id} is leveled but ships no per-level table`
          : `${unit.id} has no levels yet ships a per-level table`,
      });
    }
  });

/** A research tier's GUI art: the button background the game draws behind the tier's
 * numeral, one texture per Race, Domain and tier (declared by the player file's
 * button_background, resolved by name like every other asset). */
const ResearchTierArtSchema = z.object({ buttonBackground: ArtAssetSchema }).strict();

/** One research tier — the in-game tier button a player buys before the tier's subjects
 * open up: resources, exotics (tiers IV–V), acquire time, the research points required in
 * the domain first, and (schema v26, Issue 26) its Tier Bonuses — the tier's player_modifiers
 * composed as Effect Lines through the research composer, every label the game's own — and
 * its button background art. Identical between a race's two factions (extraction verifies
 * that, loudly). Every shipped tier carries at least one bonus; the schema still allows an
 * empty list so a future tier without one stays representable as an honest absence. */
const ResearchTierSchema = z
  .object({
    price: ResourcePriceSchema,
    exoticPrice: z.array(ExoticPriceSchema),
    acquireTime: z.number().nonnegative(),
    requiredResearchPoints: z.number().int().nonnegative(),
    bonuses: z.array(z.string().min(1)),
    art: ResearchTierArtSchema,
  })
  .strict();

/** The two game labels the tier card carries that no tier's own data names: the tooltip's
 * "Acquire Time" row label (tooltip.research.acquire_tier_time_label) and the "Tier Bonuses:"
 * header (tooltip.research_tier_modifiers_header_label), resolved once at extraction like the
 * corruption pool labels — the UI never spells them itself. */
const ResearchTierLabelsSchema = z
  .object({
    acquireTime: z.string().min(1),
    bonusesHeader: z.string().min(1),
  })
  .strict();

/** One field — a named horizontal band of the in-game tree (TEC military: MOBILIZATION,
 * EXPERIMENTAL DESIGN, ARMAMENT, ENGINEERING). `name` is the game's own label, verbatim
 * from the player file's research_fields join (the game ships them uppercase). */
const ResearchFieldSchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
  })
  .strict();

/** A research subject's GUI art. The game also ships a 459×216 tooltip_picture per subject,
 * deliberately NOT extracted in v1: nothing in the tree view renders it and the six trees'
 * tooltip pictures measure ~710 MB of PNGs against the hud icons' ~41 MB. */
const ResearchArtSchema = z.object({ hudIcon: ArtAssetSchema }).strict();

/** One research subject — a single tech in a tree. `coord` is the subject's grid position
 * within its field band: [column, row], where column is the DOMAIN-global column (two per
 * tier: floor(column / 2) === tier, proven over all 672 subjects and re-checked at
 * extraction) and row is the y-slot inside the field band. Coordinates are unique per
 * (faction, domain, field) — the game's own layout, reproduced verbatim. */
export const ResearchSubjectSchema = z
  .object({
    id: EntityId,
    name: z.string().min(1),
    // The game's own uppercase display name (name_uppercase loc key) — what the in-game
    // tree grid shows under the icon; every subject carries one.
    nameUppercase: z.string().min(1),
    // Absent when the game ships no description (257 subjects have no key; two more have an
    // empty loc value) — honest absence, never an empty string.
    description: z.string().min(1).optional(),
    // Which of the race's Factions can research it (union of the race's faction files'
    // lists with per-faction attribution, mirroring the unit roster rule).
    factions: z.array(z.enum(FACTIONS)).nonempty(),
    domain: z.enum(RESEARCH_DOMAINS),
    tier: z
      .number()
      .int()
      .min(0)
      .max(RESEARCH_TIER_COUNT - 1),
    field: EntityId,
    coord: z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
    researchTime: z.number().nonnegative(),
    price: ResourcePriceSchema,
    exoticPrice: z.array(ExoticPriceSchema),
    // Prerequisite subject ids, all required (a flat AND). The game's format allows
    // alternative prerequisite sets, but shipped data never uses more than one set
    // (0/502 at the grill sweep, re-verified at extraction — a second set fails loudly).
    prerequisites: z.array(EntityId),
    // The game's second, numeric tooltip half: composed effect lines ("Beam Weapon Damage:
    // +10%"), built at extraction from the subject file's own effect blocks with every word
    // and number the game's (labels, qualifier names and values from en.localized_text and
    // the entity files; only the line ordering and the +/%/→ glue are ours — the exact rule
    // lives in extraction's effects.ts). Empty is honest absence: unlock-style subjects
    // whose effects live on target entities compose no subject-side lines.
    effects: z.array(z.string().min(1)),
    // The reverse index's lines (schema v12, user directive): what the subject unlocks or
    // improves elsewhere — units, items, abilities, target-side gated stats, colonization,
    // features (composed in extraction's unlocks.ts from every reference site; the game's
    // own headers, prefixes, names and labels wherever the game ships them). A separate
    // field from `effects` so subject-side lines stay purely the subject file's own.
    // `unitId` (schema v18) is present exactly when the line names a roster unit, so a
    // reader can jump from a tech to what it changes; it is published by the composer that
    // already holds the id, never recovered by matching a name out of the prose.
    // `abilityId` (schema v19) is present when the line is about a unit's ability rather than
    // the unit itself. The game's tooltip shows the ABILITY's icon, name and description under
    // such a heading, so both ids ship: the ability is what the line describes, the unit is
    // where the heading points. It always resolves on that same `unitId`'s abilities.
    unlocks: z.array(
      z
        .object({
          line: z.string().min(1),
          unitId: z.string().min(1).optional(),
          abilityId: z.string().min(1).optional(),
        })
        .strict(),
    ),
    dlc: z.enum(DLCS).optional(),
    art: ResearchArtSchema,
  })
  .strict();

/** One race's tree for one domain: the race's own domain name, its field bands in the
 * game's display order, the five tier unlock costs, and every subject either faction can
 * research (filter by `factions` for a single faction's in-game tree). */
const ResearchDomainSchema = z
  .object({
    name: z.string().min(1),
    fields: z.array(ResearchFieldSchema).nonempty(),
    tiers: z.array(ResearchTierSchema).length(RESEARCH_TIER_COUNT),
    subjects: z.array(ResearchSubjectSchema),
  })
  .strict();

const ResearchRaceSchema = z
  .object({
    military: ResearchDomainSchema,
    civilian: ResearchDomainSchema,
  })
  .strict();

/** The research section: the game-wide tier names (research.uniforms tier_names → I…V) and
 * each race's two domain trees. */
const ResearchSchema = z
  .object({
    tierNames: z.array(z.string().min(1)).length(RESEARCH_TIER_COUNT),
    tierLabels: ResearchTierLabelsSchema,
    races: z
      .object({
        tec: ResearchRaceSchema,
        advent: ResearchRaceSchema,
        vasari: ResearchRaceSchema,
        eidolon: ResearchRaceSchema,
      })
      .strict(),
  })
  .strict();

const DatasetMetaSchema = z
  .object({
    // The version-by-version ledger is docs/archive/dataset-schema-versions.md; the line
    // below is the current version's own entry.
    // v43: the five cloak rung DESCRIPTIONS join `cloakLabels`. Issue 59 shipped the rung
    // names from loc but quoted the descriptions as English in `lib/disclosures.ts`, so a
    // patch rewording one would have shipped stale text silently — the structural count was
    // pinned, the wording was not. Now both sides are the game's own words.
    schemaVersion: z.literal(43),
    // Player-facing game version, read from sins2.exe's version resource (e.g. "1.61.4.0").
    gameVersion: z.string().min(1),
    // Steam depot build id from appmanifest_1575940.acf — pins the exact patch.
    steamBuildId: z.string().regex(/^\d+$/),
    extractedAt: z.iso.datetime(),
  })
  .strict();

/** The game's own labels for the three pool figures the Corruption card shows (schema v24,
 * Issue 20): `unit_modifier.max_corruption` / `.corruption_decay_rate` /
 * `.corruption_decay_delay_duration_after_gain`, markup stripped. Resolved once, here,
 * rather than repeated per unit or hardcoded in the app. */
const CorruptionPoolLabelsSchema = z
  .object({
    maxCorruption: z.string().min(1),
    decayRate: z.string().min(1),
    decayDelay: z.string().min(1),
  })
  .strict();

/** The game's own words for the two Orbit Slot kinds (schema v28, Issue 29): the build
 * group's name (`structure_build_group_name_<kind>`, shipped uppercase — the Slot type
 * facet's option, in the site's case) and the tooltip's cost label
 * (`tooltip.structure_slot_cost_labels.<kind>`, the Orbit row's label per unit). Resolved
 * once at extraction like the Corruption labels, never hardcoded in the app. A starbase's
 * virtual slot is a military one and reads under the military pair. */
const OrbitSlotLabelsSchema = z
  .object({
    civilian: z.object({ name: z.string().min(1), cost: z.string().min(1) }).strict(),
    military: z.object({ name: z.string().min(1), cost: z.string().min(1) }).strict(),
  })
  .strict();

/** The game's own words for Cloak Detection (schema v39, Issue 53, ADR 0013):
 * `tooltip.unit.detection_quality_label`, the label the game prints when a unit HAS the stat
 * — not `unit_modifier.detection_quality` ("Detection Quality"), which is what it prints
 * about a CHANGE to it. Resolved once at extraction like the Corruption and Orbit Slot
 * labels, never hardcoded in the app. */
/** The five rungs of the game's cloak ladder, lowest first — one entry per rung, so a tuple
 * rather than a list: the game names exactly five and an index IS a cloak quality. Both of
 * CloakLabelsSchema's rung fields are index-aligned to it. */
const CloakRungTuple = z.tuple([
  z.string().min(1),
  z.string().min(1),
  z.string().min(1),
  z.string().min(1),
  z.string().min(1),
]);

const CloakLabelsSchema = z
  .object({
    detection: z.string().min(1),
    /** The game's own label for the Cloaking row (schema v41, Issue 56):
     * `tooltip.unit.cloaking_label`, "Cloaking:" with the colon stripped by the shared
     * label rule — the words the game prints above Durability on a cloak-capable ship. */
    cloaking: z.string().min(1),
    /** The five rung names a cloak is rated on (schema v41, Issue 56), lowest first — the
     * INDEX is the cloak quality that rung stands for, which is how the game itself pairs
     * them ("0: Revealed" … "4: Perfect", `hud_ship.cloak_quality_level_header.*`). The
     * words here are `tooltip.unit.cloaking_status.*`, the bare name a UNIT tooltip prints:
     * Revealed, Poor, Standard, Good, Perfect. */
    rungs: CloakRungTuple,
    /** What each rung MEANS (schema v43), index-aligned with `rungs` — the game's own HUD
     * explanations of its own ladder (`hud_ship.cloak_quality_level_description.*`).
     *
     * **Nothing renders these today.** The shared rungs explainer they were published for was
     * withdrawn on the user's directive — the atlas presents data, it does not teach the
     * game's mechanics (ADR 0013's 2026-09-07 amendment). The field is kept DELIBERATELY, not
     * by oversight: dropping it is a schema bump and a regenerated dataset, a wider blast
     * radius than five short strings justify, and it is exactly what a returning explainer
     * would need. Its presence is not evidence that something displays it. */
    rungDescriptions: CloakRungTuple,
  })
  .strict();

export const DatasetSchema = z
  .object({
    meta: DatasetMetaSchema,
    orbitSlotLabels: OrbitSlotLabelsSchema,
    cloakLabels: CloakLabelsSchema,
    // The gate ledger every stored cell was folded under (schema v25): what a page folding
    // the tagged lists itself must fold under to render Formula Unverified as the extractor
    // would (ADR 0003, ADR 0009).
    verification: VerificationStateSchema,
    corruptionLabels: CorruptionPoolLabelsSchema,
    // Always all five, in EXOTIC_TYPES order — the UI joins unit exotic rows to this table
    // for display names and icons.
    exotics: z.array(ExoticInfoSchema).length(5),
    units: z.array(UnitSchema),
    // The shared ship-item table (Issue 14): exactly the items ≥1 roster unit can buy.
    items: z.array(UnitItemSchema),
    research: ResearchSchema,
  })
  .strict();

/** Every art asset reachable in a dataset value, found by shape (the ArtAssetSchema
 * fingerprint) rather than by a hand-maintained list of locations. The art copy contract
 * (extraction's artFileList) and the committed-artifact completeness test both walk THIS —
 * so a new dataset section carrying art needs zero changes at either site. Their four
 * historical extensions (units, abilities, exotics, research) each had to touch both by
 * hand; this walker is the deeper mechanism. */
export function collectArtAssets(value: unknown): ArtAsset[] {
  const found: ArtAsset[] = [];
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      for (const item of node) visit(item);
      return;
    }
    if (node === null || typeof node !== "object") return;
    const candidate = node as Record<string, unknown>;
    if (
      typeof candidate.name === "string" &&
      typeof candidate.width === "number" &&
      typeof candidate.height === "number" &&
      typeof candidate.at150 === "boolean" &&
      typeof candidate.at200 === "boolean"
    ) {
      found.push(candidate as unknown as ArtAsset);
      return;
    }
    for (const child of Object.values(candidate)) visit(child);
  };
  visit(value);
  return found;
}

/** Every (race, domain) research tree, in RACES × RESEARCH_DOMAINS order — the six-tree walk
 * the subject-name index, the subject-location index and several tests each spelled out by
 * hand, re-declaring the race and domain ids as inline literals beside the very constants
 * that define them. Iterating here means a new race or domain reaches every caller at once. */
export function researchTrees(
  research: Research,
): Array<{ race: Race; domain: ResearchDomainId; tree: ResearchDomain }> {
  return RACES.flatMap((race) =>
    RESEARCH_DOMAINS.map((domain) => ({ race, domain, tree: research.races[race][domain] })),
  );
}

/** The total fabrication price of a unit's exotic components (count × the race's per-one
 * recipe, summed per resource). The one place this math lives — extraction's cost input,
 * the entity page's fabricated totals, and the roster's exotics sort key all call it. */
export function exoticFabricationTotal(prices: readonly ExoticPrice[]): ResourcePrice {
  return prices.reduce(
    (sum, e) => ({
      credits: sum.credits + e.count * e.fabrication.credits,
      metal: sum.metal + e.count * e.fabrication.metal,
      crystal: sum.crystal + e.count * e.fabrication.crystal,
    }),
    { credits: 0, metal: 0, crystal: 0 },
  );
}

export type ResourcePrice = z.infer<typeof ResourcePriceSchema>;
export type VerificationFlagId = (typeof VERIFICATION_FLAG_IDS)[number];
export type VerificationState = z.infer<typeof VerificationStateSchema>;
export type ModifierSource = z.infer<typeof ModifierSourceSchema>;
export type TaggedModifier = z.infer<typeof TaggedModifierSchema>;
export type WeaponModifiers = z.infer<typeof WeaponModifiersSchema>;
export type CorruptionTier = (typeof CORRUPTION_TIERS)[number];
export type UnitCorruption = z.output<typeof UnitCorruptionSchema>;
export type UnitCloak = z.output<typeof UnitCloakSchema>;
export type CorruptionTierBuff = z.output<typeof CorruptionTierBuffSchema>;
export type CorruptionPoolLabels = z.output<typeof CorruptionPoolLabelsSchema>;
export type UnitStructure = z.output<typeof UnitStructureSchema>;
export type ProvidesLine = z.output<typeof ProvidesLineSchema>;
export type OrbitSlotLabels = z.output<typeof OrbitSlotLabelsSchema>;
export type CloakLabels = z.output<typeof CloakLabelsSchema>;
export type ResearchDomainId = (typeof RESEARCH_DOMAINS)[number];
/** A Research Subject's price. The same shape every other price has — the name is the
 * domain's, kept because a tree's rows are priced in it. */
export type ResearchPrice = ResourcePrice;
export type ResearchTier = z.infer<typeof ResearchTierSchema>;
export type ResearchTierLabels = z.infer<typeof ResearchTierLabelsSchema>;
export type ResearchSubject = z.infer<typeof ResearchSubjectSchema>;
export type ResearchDomain = z.infer<typeof ResearchDomainSchema>;
export type ResearchRace = z.infer<typeof ResearchRaceSchema>;
export type Research = z.infer<typeof ResearchSchema>;
export type StatCell = z.infer<typeof StatCellSchema>;
export type ArtAsset = z.infer<typeof ArtAssetSchema>;
export type UnitArt = z.infer<typeof UnitArtSchema>;
export type StrikecraftKind = (typeof STRIKECRAFT_KINDS)[number];
export type SquadronOption = z.infer<typeof SquadronOptionSchema>;
export type UnitSquadrons = z.infer<typeof UnitSquadronsSchema>;
export type Dlc = (typeof DLCS)[number];
export type ExoticType = (typeof EXOTIC_TYPES)[number];
export type ExoticPrice = z.infer<typeof ExoticPriceSchema>;
export type UnitWeapon = z.infer<typeof UnitWeaponSchema>;
export type UnitAbility = z.infer<typeof UnitAbilitySchema>;
export type GrantedAbility = z.infer<typeof GrantedAbilitySchema>;
export type AbilityEffectGroup = z.infer<typeof AbilityEffectGroupSchema>;
export type UnitItem = z.infer<typeof UnitItemSchema>;
export type UnitItemLevel = z.infer<typeof UnitItemLevelSchema>;
/** One rung of a hull's experience Level ladder (schema v36, Issue 49). */
export type UnitLevel = z.infer<typeof UnitLevelSchema>;
export type Race = (typeof RACES)[number];
export type Faction = (typeof FACTIONS)[number];
export type UnitKind = (typeof UNIT_KINDS)[number];
export type Obtained = (typeof OBTAINED_VALUES)[number];
export type Unit = z.infer<typeof UnitSchema>;
export type DatasetMeta = z.infer<typeof DatasetMetaSchema>;
export type Dataset = z.infer<typeof DatasetSchema>;
